import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  User,
  DollarSign,
  Check,
  ArrowRight,
  Search,
  Filter,
  SlidersHorizontal,
  Stethoscope,
  BedDouble,
  Plus,
  Circle,
  Trash2,
  CalendarDays,
  Clock3,
  Pencil,
  X,
} from "lucide-react";

const API_BASE = `${window.location.protocol}//${window.location.hostname}/api`;

const steps = [
  { id: 0, label: "Select visit type", icon: User },
  { id: 1, label: "Select patient", icon: DollarSign },
  { id: 2, label: "Scheme", icon: Check },
  { id: 3, label: "Check-in", icon: ArrowRight },
];

const visitTypeOptions = [
  { label: "Outpatient", value: "outpatient", icon: Stethoscope },
  { label: "Inpatient", value: "inpatient", icon: BedDouble },
  { label: "Emergency", value: "emergency", icon: Plus },
  { label: "Mortuary", value: "mortuary", icon: Circle },
];

const visitTypeLabels = {
  outpatient: "Outpatient",
  inpatient: "Inpatient",
  emergency: "Emergency",
  mortuary: "Mortuary",
};

const inputClass =
  "w-full h-10 rounded-md border border-slate-300 bg-white px-3 text-sm outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100";

const labelClass = "mb-1 block text-sm font-semibold text-slate-800";

export default function CheckInPage() {
  const [activeStep, setActiveStep] = useState(0);
  const [visitType, setVisitType] = useState("");
  const [patients, setPatients] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [sortBy, setSortBy] = useState("firstName");
  const [loadingPatients, setLoadingPatients] = useState(false);

  const [schemes, setSchemes] = useState([]);
  const [loadingSchemes, setLoadingSchemes] = useState(false);
  const [selectedSchemeId, setSelectedSchemeId] = useState(null);
  const [showSchemeForm, setShowSchemeForm] = useState(false);
  const [schemeSubmitting, setSchemeSubmitting] = useState(false);
  const [editingSchemeId, setEditingSchemeId] = useState(null);

  const [submitting, setSubmitting] = useState(false);
  const [savedCheckin, setSavedCheckin] = useState(null);

  const [schemeForm, setSchemeForm] = useState({
    schemeName: "",
    schemeCode: "",
    description: "",
    memberNumber: "",
    authorizationNumber: "",
    memberType: "",
    notes: "",
    coverExpiryDate: "",
    visitReason: "",
  });

  useEffect(() => {
    if (activeStep === 1) {
      fetchPatients();
    }
  }, [activeStep]);

  useEffect(() => {
    if (activeStep === 2 && visitType) {
      fetchSchemes(visitType);
    }
  }, [activeStep, visitType]);

  useEffect(() => {
    if (activeStep !== 1) return;

    const timer = setTimeout(() => {
      fetchPatients(searchTerm);
    }, 350);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchPatients = async (query = "") => {
    try {
      setLoadingPatients(true);

      let response;
      if (query?.trim()) {
        response = await axios.get(`${API_BASE}/patients/search`, {
          params: { q: query },
          headers: getAuthHeaders(),
        });
        setPatients(response.data?.data || []);
      } else {
        response = await axios.get(`${API_BASE}/patients`, {
          headers: getAuthHeaders(),
        });
        setPatients(Array.isArray(response.data) ? response.data : []);
      }
    } catch (error) {
      console.error("Fetch patients error:", error);
      setPatients([]);
    } finally {
      setLoadingPatients(false);
    }
  };

  const fetchSchemes = async (type = visitType) => {
    try {
      setLoadingSchemes(true);

      const res = await axios.get(`${API_BASE}/schemes`, {
        params: { visitType: type },
        headers: getAuthHeaders(),
      });

      setSchemes(res.data?.data || []);
    } catch (error) {
      console.error("Fetch schemes error:", error);
      setSchemes([]);
    } finally {
      setLoadingSchemes(false);
    }
  };

  const sortedPatients = useMemo(() => {
    const field = sortBy === "firstName" ? "firstName" : "lastName";
    return [...patients].sort((a, b) => {
      const va = (a[field] || "").toLowerCase();
      const vb = (b[field] || "").toLowerCase();
      return va.localeCompare(vb);
    });
  }, [patients, sortBy]);

  const handleVisitTypeSelect = (type) => {
    setVisitType(type);
    setSelectedPatient(null);
    setSelectedSchemeId(null);
    setSchemes([]);
    setSavedCheckin(null);
    setShowSchemeForm(false);
    setEditingSchemeId(null);
    setSchemeForm({
      schemeName: "",
      schemeCode: "",
      description: "",
      memberNumber: "",
      authorizationNumber: "",
      memberType: "",
      notes: "",
      coverExpiryDate: "",
      visitReason: "",
    });
    setActiveStep(1);
  };

  const handlePatientSelect = (patient) => {
    setSelectedPatient(patient);
  };

  const handleContinueFromPatient = () => {
    if (!selectedPatient) {
      alert("Please select patient first");
      return;
    }
    setActiveStep(2);
  };

  const resetSchemeForm = () => {
    setSchemeForm({
      schemeName: "",
      schemeCode: "",
      description: "",
      memberNumber: "",
      authorizationNumber: "",
      memberType: "",
      notes: "",
      coverExpiryDate: "",
      visitReason: "",
    });
    setEditingSchemeId(null);
  };

  const handleSchemeChange = (e) => {
    const { name, value } = e.target;
    setSchemeForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSchemeSelect = (scheme) => {
    setSelectedSchemeId(scheme.id);
    setShowSchemeForm(true);
    setEditingSchemeId(scheme.id);
    setSchemeForm((prev) => ({
      ...prev,
      schemeName: scheme.schemeName || "",
      schemeCode: scheme.schemeCode || "",
      description: scheme.description || "",
    }));
  };

  const handleAddScheme = () => {
    setSelectedSchemeId(null);
    setShowSchemeForm(true);
    resetSchemeForm();
  };

  const handleSaveScheme = async () => {
    if (!schemeForm.schemeName.trim()) {
      alert("Scheme name is required");
      return;
    }

    if (!visitType) {
      alert("Visit type is required");
      return;
    }

    try {
      setSchemeSubmitting(true);

      const payload = {
        schemeName: schemeForm.schemeName,
        schemeCode: schemeForm.schemeCode || null,
        description: schemeForm.description || null,
        visitType,
      };

      let res;
      if (editingSchemeId) {
        res = await axios.put(`${API_BASE}/schemes/${editingSchemeId}`, payload, {
          headers: getAuthHeaders(),
        });
      } else {
        res = await axios.post(`${API_BASE}/schemes`, payload, {
          headers: getAuthHeaders(),
        });
      }

      await fetchSchemes(visitType);

      if (res.data?.data?.id) {
        setSelectedSchemeId(res.data.data.id);
      }

      alert(res.data?.message || "Scheme saved successfully");
    } catch (error) {
      console.error("Save scheme error:", error);
      alert(error?.response?.data?.message || "Failed to save scheme");
    } finally {
      setSchemeSubmitting(false);
    }
  };

  const handleDeleteScheme = async (schemeId) => {
    const confirmDelete = window.confirm("Delete this scheme?");
    if (!confirmDelete) return;

    try {
      await axios.delete(`${API_BASE}/schemes/${schemeId}`, {
        headers: getAuthHeaders(),
      });

      if (selectedSchemeId === schemeId) {
        setSelectedSchemeId(null);
        setShowSchemeForm(false);
        resetSchemeForm();
      }

      await fetchSchemes(visitType);
      alert("Scheme deleted successfully");
    } catch (error) {
      console.error("Delete scheme error:", error);
      alert(error?.response?.data?.message || "Failed to delete scheme");
    }
  };

  const handleContinueToCheckin = () => {
    if (!selectedPatient) {
      alert("Please select patient");
      return;
    }
    setActiveStep(3);
  };

  const handleFinalCheckIn = async () => {
    if (!selectedPatient || !visitType) {
      alert("Patient and visit type required");
      return;
    }

    try {
      setSubmitting(true);

      const selectedScheme = schemes.find((s) => s.id === selectedSchemeId);

      const payload = {
        patientId: selectedPatient.id,
        visitType,
        schemeId: selectedScheme?.id || null,
        schemeName: selectedScheme?.schemeName || schemeForm.schemeName || null,
        visitReason: schemeForm.visitReason || null,
        memberNumber: schemeForm.memberNumber || null,
        authorizationNumber: schemeForm.authorizationNumber || null,
        memberType: schemeForm.memberType || null,
        notes: schemeForm.notes || null,
        coverExpiryDate: schemeForm.coverExpiryDate || null,
      };

      const res = await axios.post(`${API_BASE}/checkin`, payload, {
        headers: getAuthHeaders(),
      });

      setSavedCheckin(res.data?.data || null);
      alert(res.data?.message || "Patient checked in successfully");
    } catch (error) {
      console.error("Check-in error:", error);
      alert(error?.response?.data?.message || "Check-in failed");
    } finally {
      setSubmitting(false);
    }
  };

  const calculateAgeText = (dateOfBirth) => {
    if (!dateOfBirth) return "";
    const dob = new Date(dateOfBirth);
    if (Number.isNaN(dob.getTime())) return "";

    const now = new Date();
    let years = now.getFullYear() - dob.getFullYear();
    let months = now.getMonth() - dob.getMonth();

    if (months < 0) {
      years--;
      months += 12;
    }

    return `(${years} yr ${months} mo)`;
  };

  const renderTopPatientHeader = () => {
    if (!selectedPatient) return null;

    return (
      <div className="mb-4 rounded-sm border border-slate-300 bg-slate-100 px-6 py-5">
        <h1 className="text-[22px] font-bold uppercase tracking-wide text-slate-700">
          {selectedPatient.firstName} {selectedPatient.lastName}
        </h1>

        <p className="mt-1 text-sm font-semibold text-slate-700">
          {selectedPatient.dateOfBirth || selectedPatient.birthDate || "-"}{" "}
          {calculateAgeText(selectedPatient.dateOfBirth || selectedPatient.birthDate)},{" "}
          {selectedPatient.gender || "-"} -{" "}
          {selectedPatient.medicalRecordNumber || selectedPatient.mrn || "-"}
        </p>

        <div className="mt-3 flex flex-wrap gap-8 text-sm">
          <div>
            <p className="font-bold text-slate-800">National ID</p>
            <p className="mt-1 text-slate-700">{selectedPatient.nationalId || ""}</p>
          </div>
          <div>
            <p className="font-bold text-slate-800">Mobile phone</p>
            <p className="mt-1 text-slate-700">{selectedPatient.phone || "-"}</p>
          </div>
          <div>
            <p className="font-bold text-slate-800">Address</p>
            <p className="mt-1 text-slate-700">
              {selectedPatient.addressLine || selectedPatient.address || ""}
            </p>
          </div>
        </div>
      </div>
    );
  };

  const renderStepper = () => {
    return (
      <div className="rounded-sm border border-slate-200 bg-white px-4 pt-5 pb-4">
        <div className="grid grid-cols-4 items-start">
          {steps.map((step, index) => {
            const Icon = step.icon;
            const isDone = index < activeStep;
            const isActive = index === activeStep;
            const isPassed = index <= activeStep;

            return (
              <div key={step.id} className="relative text-center">
                <div className="mb-2 flex flex-col items-center justify-center gap-1">
                  <Icon className={`h-5 w-5 ${isActive ? "text-violet-600" : "text-black"}`} />
                  <p className={`text-[14px] font-semibold ${isActive ? "text-violet-600" : "text-black"}`}>
                    {step.label}
                  </p>
                </div>

                <div className="relative flex items-center justify-center">
                  {index !== 0 && (
                    <div className={`absolute right-1/2 h-[6px] w-full ${isPassed ? "bg-violet-500" : "bg-slate-200"}`} />
                  )}
                  {index !== steps.length - 1 && (
                    <div className={`absolute left-1/2 h-[6px] w-full ${index < activeStep ? "bg-violet-500" : "bg-slate-200"}`} />
                  )}

                  <div
                    className={`relative z-10 h-5 w-5 rounded-full border-4 ${
                      isDone
                        ? "border-violet-500 bg-violet-500"
                        : isActive
                        ? "border-violet-500 bg-white"
                        : "border-slate-300 bg-slate-300"
                    }`}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderVisitTypeStep = () => {
    return (
      <div className="flex min-h-[520px] items-center justify-center">
        <div className="flex flex-wrap items-center justify-center gap-4">
          {visitTypeOptions.map((item) => {
            const Icon = item.icon;
            const selected = visitType === item.value;

            return (
              <button
                key={item.value}
                type="button"
                onClick={() => handleVisitTypeSelect(item.value)}
                className={`inline-flex min-w-[160px] items-center justify-center gap-2 rounded-md px-6 py-3 text-lg font-semibold transition ${
                  selected
                    ? "bg-violet-700 text-white"
                    : item.value === "mortuary"
                    ? "bg-slate-200 text-slate-600 hover:bg-slate-300"
                    : "bg-violet-600 text-white hover:bg-violet-700"
                }`}
              >
                <Icon className="h-5 w-5" />
                {item.label}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const renderPatientListStep = () => {
    return (
      <div className="mt-4 rounded-sm border border-slate-200 bg-white">
        {!selectedPatient ? (
          <>
            <div className="border-b border-slate-200 px-4 py-4">
              <div className="flex items-center justify-between gap-3">
                <div className="relative w-full">
                  <Search className="absolute left-0 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-700" />
                  <input
                    type="text"
                    placeholder="Search"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full border-none bg-transparent pl-8 pr-4 text-[30px] font-normal text-slate-500 outline-none"
                  />
                </div>
                <Filter className="h-5 w-5 text-slate-900" />
              </div>
            </div>

            <div className="flex items-center justify-between border-b border-slate-200 px-1 py-1">
              <button className="rounded border border-slate-300 px-2 py-1 text-xs font-medium text-slate-600">
                Search in
              </button>

              <div className="flex items-center gap-2">
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="rounded border border-slate-300 px-2 py-1 text-xs text-slate-700 outline-none"
                >
                  <option value="firstName">First name</option>
                  <option value="lastName">Family name</option>
                </select>

                <button className="rounded border border-slate-300 p-1">
                  <SlidersHorizontal className="h-4 w-4 text-slate-600" />
                </button>
              </div>
            </div>

            <div className="max-h-[620px] overflow-y-auto">
              {loadingPatients ? (
                <div className="px-6 py-8 text-sm text-slate-500">Loading patients...</div>
              ) : sortedPatients.length === 0 ? (
                <div className="px-6 py-8 text-sm text-slate-500">No patients found</div>
              ) : (
                sortedPatients.map((patient) => (
                  <button
                    key={patient.id}
                    type="button"
                    onClick={() => handlePatientSelect(patient)}
                    className="flex w-full items-start justify-between border-b border-slate-200 px-4 py-4 text-left hover:bg-slate-50"
                  >
                    <div className="flex items-start gap-3">
                      <div className="mt-1 flex h-8 w-8 items-center justify-center rounded-full bg-slate-400 text-white">
                        <User className="h-5 w-5" />
                      </div>

                      <div>
                        <h3 className="text-[18px] font-bold uppercase text-slate-900">
                          {patient.firstName} {patient.lastName}
                        </h3>
                        <p className="text-[15px] text-slate-900">
                          MRN: {patient.medicalRecordNumber || patient.mrn || "-"}
                        </p>
                        <p className="text-[15px] text-slate-900">
                          Phone: {patient.phone || "-"}
                        </p>
                      </div>
                    </div>

                    <div className="pt-6 text-[15px] text-slate-900">Active</div>
                  </button>
                ))
              )}
            </div>
          </>
        ) : (
          <div className="p-4">
            <div className="mb-5 flex gap-3">
              <button
                type="button"
                onClick={() => setSelectedPatient(null)}
                className="rounded bg-slate-200 px-5 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-300"
              >
                Select different patient
              </button>

              <button
                type="button"
                className="rounded bg-slate-200 px-5 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-300"
              >
                Edit patient details
              </button>
            </div>

            <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1.2fr_1fr]">
              <div className="grid grid-cols-[170px_1fr] gap-4">
                <div className="space-y-16 text-right text-[16px] font-medium text-slate-700">
                  <p>Demographic details</p>
                  <p>Patient account</p>
                  <p>Identification details</p>
                </div>

                <div className="border-l border-slate-300 pl-4">
                  <div className="mb-8">
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <Info label="Title" value={selectedPatient.title || "Mr"} />
                      <Info label="First name" value={selectedPatient.firstName || "-"} />
                      <Info label="Family name" value={selectedPatient.lastName || "-"} />
                      <Info label="Gender" value={selectedPatient.gender || "-"} />
                      <Info label="Birth date" value={selectedPatient.dateOfBirth || "-"} />
                      <Info label="Medical record number" value={selectedPatient.medicalRecordNumber || selectedPatient.mrn || "-"} />
                    </div>
                  </div>

                  <div className="mb-8">
                    <div className="grid grid-cols-1 gap-4 text-sm">
                      <Info label="Patient groups" value={selectedPatient.patientGroups || "none"} />
                      <Info label="Patient account status" value={selectedPatient.patientAccountStatus || "Active"} />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-4 text-sm">
                    <Info label="National ID" value={selectedPatient.nationalId || "-"} />
                    <Info label="Passport" value={selectedPatient.passport || "-"} />
                    <Info label="Drivers licence" value={selectedPatient.driversLicence || "-"} />
                  </div>
                </div>
              </div>

              <div className="border-l border-slate-300 pl-4">
                <div className="space-y-6 text-sm">
                  <Info label="Last visit date" value={selectedPatient.lastVisitDate || "-"} />
                  <Info label="Visit type" value={selectedPatient.lastVisitType || "Outpatient"} />
                  <Info label="Price lists" value="Default price list" />
                  <Info label="Doctor name" value={selectedPatient.doctorName || ""} />
                  <Info label="Payment method" value="Cash" />
                  <Info label="Total invoice amount" value="US$0.00" />
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                type="button"
                onClick={handleContinueFromPatient}
                className="rounded bg-violet-600 px-5 py-2 text-sm font-semibold text-white hover:bg-violet-700"
              >
                Continue to Scheme
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderVisitSpecificFields = () => {
    if (visitType === "mortuary") {
      return (
        <div className="mb-4">
          <label className={labelClass}>Mortuary reason</label>
          <input
            type="text"
            name="visitReason"
            value={schemeForm.visitReason}
            onChange={handleSchemeChange}
            placeholder="Enter mortuary reason"
            className={inputClass}
          />
        </div>
      );
    }

    if (visitType === "emergency") {
      return (
        <div className="mb-4">
          <label className={labelClass}>Emergency reason</label>
          <input
            type="text"
            name="visitReason"
            value={schemeForm.visitReason}
            onChange={handleSchemeChange}
            placeholder="Enter emergency reason"
            className={inputClass}
          />
        </div>
      );
    }

    if (visitType === "inpatient") {
      return (
        <div className="mb-4">
          <label className={labelClass}>Admission reason</label>
          <input
            type="text"
            name="visitReason"
            value={schemeForm.visitReason}
            onChange={handleSchemeChange}
            placeholder="Enter admission reason"
            className={inputClass}
          />
        </div>
      );
    }

    return (
      <div className="mb-4">
        <label className={labelClass}>Consultation reason</label>
        <input
          type="text"
          name="visitReason"
          value={schemeForm.visitReason}
          onChange={handleSchemeChange}
          placeholder="Enter consultation reason"
          className={inputClass}
        />
      </div>
    );
  };

  const renderSchemeStep = () => {
    return (
      <div className="mt-4 rounded-sm border border-slate-200 bg-white p-4">
        <div className="mb-4">
          <h3 className="text-lg font-bold text-slate-800">
            {visitTypeLabels[visitType]} Schemes
          </h3>
          <p className="text-sm text-slate-500">
            Only {visitTypeLabels[visitType]?.toLowerCase()} related schemes are displayed
          </p>
        </div>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[300px_1fr]">
          <div className="border border-slate-200">
            {loadingSchemes ? (
              <div className="p-4 text-sm text-slate-500">Loading schemes...</div>
            ) : schemes.length === 0 ? (
              <div className="p-4 text-sm text-slate-500">No schemes found</div>
            ) : (
              schemes.map((scheme, index) => (
                <div
                  key={scheme.id}
                  className={`flex items-start justify-between border-b border-slate-200 px-3 py-3 ${
                    selectedSchemeId === scheme.id ? "bg-slate-100" : ""
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => handleSchemeSelect(scheme)}
                    className="flex flex-1 gap-3 text-left"
                  >
                    <span className="text-[16px] font-semibold text-slate-900">
                      {index + 1}
                    </span>
                    <div>
                      <span className="block text-[15px] font-medium uppercase text-slate-900">
                        {scheme.schemeName}
                      </span>
                      <span className="text-xs text-slate-500">
                        {scheme.visitType}
                      </span>
                    </div>
                  </button>

                  <div className="ml-2 flex items-center gap-2">
                    <button type="button" onClick={() => handleSchemeSelect(scheme)}>
                      <Pencil className="h-4 w-4 text-blue-600" />
                    </button>
                    <button type="button" onClick={() => handleDeleteScheme(scheme.id)}>
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </button>
                  </div>
                </div>
              ))
            )}

            <div className="p-3 text-center">
              <button
                type="button"
                onClick={handleAddScheme}
                className="rounded bg-violet-600 px-6 py-2 text-sm font-semibold text-white hover:bg-violet-700"
              >
                Add {visitTypeLabels[visitType]} Scheme
              </button>
            </div>
          </div>

          <div>
            {!showSchemeForm ? (
              <div className="flex h-full min-h-[500px] items-center justify-center text-[18px] font-semibold text-slate-900">
                No scheme is selected
              </div>
            ) : (
              <div>
                <div className="mb-4 flex items-center justify-between">
                  <h3 className="text-lg font-bold text-slate-800">
                    {editingSchemeId ? "Edit Scheme" : "Add Scheme"}
                  </h3>

                  <button
                    type="button"
                    onClick={() => setShowSchemeForm(false)}
                    className="rounded p-1 hover:bg-slate-100"
                  >
                    <X className="h-4 w-4 text-slate-600" />
                  </button>
                </div>

                <div className="mb-4">
                  <label className={labelClass}>Scheme name</label>
                  <input
                    type="text"
                    name="schemeName"
                    value={schemeForm.schemeName}
                    onChange={handleSchemeChange}
                    className={inputClass}
                  />
                </div>

                <div className="mb-4">
                  <label className={labelClass}>Scheme code</label>
                  <input
                    type="text"
                    name="schemeCode"
                    value={schemeForm.schemeCode}
                    onChange={handleSchemeChange}
                    className={inputClass}
                  />
                </div>

                <div className="mb-4">
                  <label className={labelClass}>Description</label>
                  <textarea
                    name="description"
                    value={schemeForm.description}
                    onChange={handleSchemeChange}
                    rows={3}
                    className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100"
                  />
                </div>

                <hr className="my-5" />

                {renderVisitSpecificFields()}

                <div className="mb-4">
                  <label className={labelClass}>Member #</label>
                  <input
                    type="text"
                    name="memberNumber"
                    value={schemeForm.memberNumber}
                    onChange={handleSchemeChange}
                    className={inputClass}
                  />
                </div>

                <div className="mb-4">
                  <label className={labelClass}>Visit authorization number</label>
                  <input
                    type="text"
                    name="authorizationNumber"
                    value={schemeForm.authorizationNumber}
                    onChange={handleSchemeChange}
                    className={inputClass}
                  />
                </div>

                <div className="mb-4">
                  <label className={labelClass}>Member type</label>
                  <select
                    name="memberType"
                    value={schemeForm.memberType}
                    onChange={handleSchemeChange}
                    className={inputClass}
                  >
                    <option value="">Select...</option>
                    <option value="principal">Principal</option>
                    <option value="dependent">Dependent</option>
                    <option value="spouse">Spouse</option>
                  </select>
                </div>

                <div className="mb-4">
                  <label className={labelClass}>Notes</label>
                  <textarea
                    name="notes"
                    value={schemeForm.notes}
                    onChange={handleSchemeChange}
                    rows={5}
                    className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100"
                  />
                </div>

                <div className="mb-6">
                  <label className={labelClass}>Cover expiry date</label>
                  <div className="relative">
                    <input
                      type="date"
                      name="coverExpiryDate"
                      value={schemeForm.coverExpiryDate}
                      onChange={handleSchemeChange}
                      className={`${inputClass} pr-10`}
                    />
                    <CalendarDays className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <button
                    type="button"
                    onClick={() => setActiveStep(1)}
                    className="rounded bg-slate-200 px-5 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-300"
                  >
                    Back
                  </button>

                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={handleSaveScheme}
                      disabled={schemeSubmitting}
                      className="rounded bg-blue-600 px-5 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
                    >
                      {schemeSubmitting ? "Saving..." : "Save Scheme"}
                    </button>

                    <button
                      type="button"
                      onClick={handleContinueToCheckin}
                      className="rounded bg-violet-600 px-5 py-2 text-sm font-semibold text-white hover:bg-violet-700"
                    >
                      Continue to Check-in
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderFinalStep = () => {
    const finalScheme = schemes.find((s) => s.id === selectedSchemeId);

    return (
      <div className="mt-4 rounded-sm border border-slate-200 bg-white px-5 py-6">
        <h2 className="mb-6 text-[16px] font-bold text-slate-800">
          Patient Checked in
        </h2>

        <div className="flex flex-wrap items-start gap-8 text-sm">
          <SummaryCol
            label="Check in ticket number"
            value={savedCheckin?.ticketNumber || "-"}
          />
          <SummaryCol
            label="Visit number"
            value={savedCheckin?.visitNumber || "-"}
          />
          <div>
            <p className="mb-2 font-semibold text-slate-700">Time in hospital</p>
            <span className="inline-flex items-center gap-2 rounded bg-violet-600 px-3 py-1 text-xs font-semibold text-white">
              <Clock3 className="h-3 w-3" />
              Current visit
            </span>
          </div>
          <SummaryCol label="Visit type" value={capitalize(visitType)} />
          <SummaryCol
            label="Visit reason"
            value={
              schemeForm.visitReason ||
              (visitType === "mortuary"
                ? "Mortuary storage"
                : visitType === "emergency"
                ? "Emergency visit"
                : visitType === "inpatient"
                ? "Inpatient admission"
                : "Outpatient consultation")
            }
          />
          <SummaryCol
            label="Scheme"
            value={finalScheme?.schemeName || schemeForm.schemeName || "N/A"}
          />
        </div>

        <div className="mt-6">
          <p className="mb-2 font-semibold text-slate-700">Visit notes</p>
          <p className="text-sm text-slate-700">{schemeForm.notes || "N/A"}</p>
        </div>

        <div className="mt-8 flex justify-end gap-3">
          {!savedCheckin ? (
            <>
              <button
                type="button"
                onClick={() => setActiveStep(2)}
                className="rounded bg-slate-200 px-5 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-300"
              >
                Back
              </button>

              <button
                type="button"
                onClick={handleFinalCheckIn}
                disabled={submitting}
                className="rounded bg-violet-600 px-6 py-2 text-sm font-semibold text-white hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {submitting ? "Submitting..." : "Confirm Check-in"}
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={() => {
                setActiveStep(0);
                setVisitType("");
                setPatients([]);
                setSearchTerm("");
                setSelectedPatient(null);
                setSelectedSchemeId(null);
                setShowSchemeForm(false);
                setSavedCheckin(null);
                setEditingSchemeId(null);
                setSchemes([]);
                setSchemeForm({
                  schemeName: "",
                  schemeCode: "",
                  description: "",
                  memberNumber: "",
                  authorizationNumber: "",
                  memberType: "",
                  notes: "",
                  coverExpiryDate: "",
                  visitReason: "",
                });
              }}
              className="rounded bg-emerald-600 px-6 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
            >
              New Check-in
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#f7f7f7] p-2 sm:p-3 md:p-4">
      {renderTopPatientHeader()}

      <div className="rounded-sm border border-slate-200 bg-white p-1 sm:p-2">
        {renderStepper()}
        {activeStep === 0 && renderVisitTypeStep()}
        {activeStep === 1 && renderPatientListStep()}
        {activeStep === 2 && renderSchemeStep()}
        {activeStep === 3 && renderFinalStep()}
      </div>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <p className="mb-1 text-[14px] font-bold text-slate-900">{label}</p>
      <p className="text-[14px] text-slate-900">{value}</p>
    </div>
  );
}

function SummaryCol({ label, value }) {
  return (
    <div>
      <p className="mb-1 font-semibold text-slate-700">{label}</p>
      <p className="text-[18px] text-slate-900">{value}</p>
    </div>
  );
}

function capitalize(value) {
  if (!value) return "-";
  return value.charAt(0).toUpperCase() + value.slice(1);
}